import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Markdown from "@/components/Markdown";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, Loader2, Building2, Search, Bot, Network, Download, Play } from "lucide-react";
import ReportActions from "@/components/ReportActions";

const ICONS = { company: Building2, seo: Search, scraper: Network, agent: Bot };

const PLACEHOLDERS = {
  company: { field: "name", label: "Rakip şirket adı veya URL", ph: "Örn: kuzey.com veya Kuzey Holding" },
  seo: { field: "url", label: "URL", ph: "https://ornek.com" },
  scraper: { field: "url", label: "URL", ph: "https://ornek.com" },
  agent: { field: "goal", label: "Hedef", ph: "Örn: Bu hafta 3 sektör raporu yayınla" },
};

export default function ServiceDetail() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [downloads, setDownloads] = useState({});
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!["company", "seo", "scraper", "agent"].includes(key)) { navigate("/dashboard"); return; }
    api.get("/services/downloads").then((r) => setDownloads(r.data.downloads || {})).catch(() => {});
    setInput(""); setResult(null); setProgress(0);
  }, [key, navigate]);

  const Icon = ICONS[key];
  const downloadUrl = (downloads[key] || "").trim();
  const meta = PLACEHOLDERS[key];

  const run = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setRunning(true); setResult(null); setProgress(5);
    const timer = setInterval(() => setProgress((p) => Math.min(90, p + Math.random() * 8)), 900);
    try {
      let data;
      if (key === "company") {
        // Accept either URL or plain name
        const looksUrl = /^https?:\/\//i.test(input) || input.includes(".");
        const payload = looksUrl ? { name: input, url: input } : { name: input };
        data = (await api.post("/services/company-analysis", payload)).data;
      } else if (key === "seo") {
        data = (await api.post("/services/seo-analysis", { url: input })).data;
      } else if (key === "scraper") {
        data = (await api.post("/services/scraper", { url: input })).data;
      } else if (key === "agent") {
        data = (await api.post("/services/agent", { goal: input })).data;
      }
      setProgress(100);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      clearInterval(timer);
      setRunning(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid={`service-${key}-page`}>
      <Header />
      <main className="flex-1 max-w-5xl w-full mx-auto px-5 md:px-8 py-12 md:py-16">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 font-mono-aq text-[0.7rem] uppercase tracking-[0.2em] text-[#4A4A4A] hover:text-[#0A0A0A] mb-8" data-testid="back-to-dashboard">
          <ArrowLeft size={14} /> {t.dashboard.back}
        </button>

        <div className="flex items-start gap-5 mb-10">
          <div className="w-16 h-16 rounded-2xl border border-[#B6B09F] flex items-center justify-center text-[#1E3A5F]"><Icon size={26} /></div>
          <div>
            <h1 className="font-serif-display text-4xl md:text-5xl tracking-tighter">{t.dashboard.service_titles[key]}</h1>
            <p className="text-[#4A4A4A] mt-3 max-w-xl leading-relaxed">{t.dashboard.service_desc[key]}</p>
          </div>
        </div>

        {/* Analiz formu */}
        <form onSubmit={run} className="aq-card p-6 md:p-8 mb-6" data-testid="service-form">
          <label className="label-eyebrow block mb-2">{meta.label}</label>
          <div className="flex flex-wrap gap-3 items-end">
            <input required value={input} onChange={(e) => setInput(e.target.value)} placeholder={meta.ph}
              className="flex-1 min-w-[240px] bg-transparent border-b border-[#B6B09F] focus:border-[#1E3A5F] outline-none py-2"
              data-testid="service-input" />
            <button type="submit" disabled={running} className="aq-button-primary" data-testid="service-run">
              {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              {running ? "Analiz ediliyor..." : "Analizi Başlat"}
            </button>
          </div>
          {running || progress > 0 ? (
            <div className="mt-5 space-y-2" data-testid="progress-bar">
              <div className="h-1.5 bg-[#EAE4D5] rounded-full overflow-hidden">
                <div className="h-full bg-[#1E3A5F] transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <div className="font-mono-aq text-[0.65rem] tracking-[0.2em] uppercase text-[#4A4A4A]">
                {progress < 30 && "Hedef taranıyor..."}
                {progress >= 30 && progress < 60 && "Veri toplanıyor..."}
                {progress >= 60 && progress < 95 && "AI değerlendirmesi..."}
                {progress >= 95 && "Rapor derleniyor..."}
              </div>
            </div>
          ) : null}
        </form>

        {/* Sonuç */}
        {result && (
          <section className="aq-card p-6 md:p-10 mb-8" data-testid="service-result">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="label-eyebrow">— Rapor</div>
              {result.id && <ReportActions resultId={result.id} />}
            </div>
            {result.report && <Markdown>{result.report}</Markdown>}
            {result.metrics && (
              <details className="mt-6">
                <summary className="cursor-pointer text-sm font-mono-aq tracking-wider uppercase text-[#4A4A4A]">Ham metrikler</summary>
                <pre className="mt-3 text-xs bg-[#EAE4D5] rounded p-4 overflow-x-auto">{JSON.stringify(result.metrics, null, 2)}</pre>
              </details>
            )}
            {result.result && !result.report && (
              <pre className="text-xs bg-[#EAE4D5] rounded p-4 overflow-x-auto" data-testid="scraper-raw">{JSON.stringify(result.result, null, 2)}</pre>
            )}
          </section>
        )}

        {/* İkincil: masaüstü uygulaması opsiyonu */}
        {downloadUrl && (
          <section className="aq-card p-6 flex flex-wrap items-center justify-between gap-4" data-testid="download-secondary">
            <div>
              <div className="label-eyebrow mb-1">Masaüstü Uygulaması</div>
              <div className="text-sm text-[#4A4A4A]">Offline / batch işleri için indirilebilir sürüm mevcut.</div>
            </div>
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="aq-button-ghost" data-testid="service-download-btn">
              {t.dashboard.download} <Download size={14} />
            </a>
          </section>
        )}

        {/* Diğer servisler */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.keys(t.dashboard.service_titles).filter(k => k !== key).map((k) => {
            const KIcon = ICONS[k];
            return (
              <button key={k} onClick={() => navigate(`/dashboard/services/${k}`)}
                className="aq-card p-5 text-left flex items-center gap-4 hover:border-[#0A0A0A]"
                data-testid={`other-service-${k}`}>
                <div className="w-10 h-10 rounded-full border border-[#B6B09F] flex items-center justify-center text-[#1E3A5F]"><KIcon size={16} /></div>
                <div>
                  <div className="font-serif-display text-lg">{t.dashboard.service_titles[k]}</div>
                  <div className="font-mono-aq text-[0.65rem] tracking-[0.2em] uppercase text-[#B6B09F] mt-1">{t.dashboard.open} <ArrowRight size={12} className="inline" /></div>
                </div>
              </button>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
